import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import Layout from '@/components/Layout/Layout';
import { CompanyProvider } from '@/contexts/CompanyContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/Auth';
import Dashboard from '@/pages/dashboard';
import { Properties, PropertyDetail, PropertyForm } from '@/pages/properties';
import { RoomTypes, RoomTypeForm, RoomTypeDetail } from '@/pages/roomTypes';
import { RatePlans, RatePlanDetail, RatePlanForm, AvailabilityCalendar, Restrictions, Inventory } from '@/pages/inventory';
import { Reservations, ReservationDetail } from '@/pages/reservations';
import { Channels } from '@/pages/channels';
import { Settings } from '@/pages/settings';
import { WebhookLogs, WebhookManagement } from '@/pages/webhooks';
import CancellationPolicies from '@/pages/policies/CancellationPolicies';
import CancellationPolicyForm from '@/components/policies/CancellationPolicyForm';
import CancellationPolicyDetail from '@/components/policies/CancellationPolicyDetail';
import Taxes from '@/pages/taxes/Taxes';
import TaxForm from '@/components/taxes/TaxForm';
import TaxDetail from '@/components/taxes/TaxDetail';
import HotelPolicies from '@/pages/policies/HotelPolicies';
import HotelPolicyForm from '@/components/policies/HotelPolicyForm';
import HotelPolicyDetail from '@/components/policies/HotelPolicyDetail';
import '@/styles/globals.css';
// Create a custom theme that works well with Tailwind
var theme = createTheme({
    palette: {
        primary: {
            main: '#0ea5e9', // sky-500
            light: '#38bdf8', // sky-400
            dark: '#0284c7', // sky-600
        },
        secondary: {
            main: '#64748b', // slate-500
            light: '#94a3b8', // slate-400
            dark: '#475569', // slate-600
        },
        background: {
            default: '#f8fafc', // slate-50
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 8,
                    fontWeight: 500,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#0ea5e9', // sky-500
                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                },
            },
        },
    },
});
// Create QueryClient instance
var queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 2,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
        },
    },
});
var App = function () {
    return (_jsx(QueryClientProvider, { client: queryClient, children: _jsx(AuthProvider, { children: _jsx(CompanyProvider, { children: _jsxs(ThemeProvider, { theme: theme, children: [_jsx(CssBaseline, {}), _jsx(Router, { basename: "/app", children: _jsx(ProtectedRoute, { children: _jsx(Layout, { children: _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(Dashboard, {}) }), _jsx(Route, { path: "/properties", element: _jsx(Properties, {}) }), _jsx(Route, { path: "/properties/new", element: _jsx(PropertyForm, {}) }), _jsx(Route, { path: "/properties/:id", element: _jsx(PropertyDetail, {}) }), _jsx(Route, { path: "/properties/:id/edit", element: _jsx(PropertyForm, {}) }), _jsx(Route, { path: "/room-types", element: _jsx(RoomTypes, {}) }), _jsx(Route, { path: "/room-types/new", element: _jsx(RoomTypeForm, {}) }), _jsx(Route, { path: "/room-types/:id", element: _jsx(RoomTypeDetail, {}) }), _jsx(Route, { path: "/room-types/:id/edit", element: _jsx(RoomTypeForm, {}) }), _jsx(Route, { path: "/rate-plans", element: _jsx(RatePlans, {}) }), _jsx(Route, { path: "/rate-plans/new", element: _jsx(RatePlanForm, {}) }), _jsx(Route, { path: "/rate-plans/:id", element: _jsx(RatePlanDetail, {}) }), _jsx(Route, { path: "/rate-plans/:id/edit", element: _jsx(RatePlanForm, {}) }), _jsx(Route, { path: "/inventory", element: _jsx(Inventory, {}) }), _jsx(Route, { path: "/availability", element: _jsx(AvailabilityCalendar, {}) }), _jsx(Route, { path: "/restrictions", element: _jsx(Restrictions, {}) }), _jsx(Route, { path: "/reservations", element: _jsx(Reservations, {}) }), _jsx(Route, { path: "/reservations/:id", element: _jsx(ReservationDetail, {}) }), _jsx(Route, { path: "/channels", element: _jsx(Channels, {}) }), _jsx(Route, { path: "/webhooks", element: _jsx(WebhookManagement, {}) }), _jsx(Route, { path: "/webhooks_logs", element: _jsx(WebhookLogs, {}) }), _jsx(Route, { path: "/cancellation-policies", element: _jsx(CancellationPolicies, {}) }), _jsx(Route, { path: "/cancellation-policies/new", element: _jsx(CancellationPolicyForm, {}) }), _jsx(Route, { path: "/cancellation-policies/:id", element: _jsx(CancellationPolicyDetail, {}) }), _jsx(Route, { path: "/cancellation-policies/:id/edit", element: _jsx(CancellationPolicyForm, {}) }), _jsx(Route, { path: "/taxes", element: _jsx(Taxes, {}) }), _jsx(Route, { path: "/taxes/new", element: _jsx(TaxForm, {}) }), _jsx(Route, { path: "/taxes/:id", element: _jsx(TaxDetail, {}) }), _jsx(Route, { path: "/taxes/:id/edit", element: _jsx(TaxForm, {}) }), _jsx(Route, { path: "/hotel-policies", element: _jsx(HotelPolicies, {}) }), _jsx(Route, { path: "/hotel-policies/new", element: _jsx(HotelPolicyForm, {}) }), _jsx(Route, { path: "/hotel-policies/:id", element: _jsx(HotelPolicyDetail, {}) }), _jsx(Route, { path: "/hotel-policies/:id/edit", element: _jsx(HotelPolicyForm, {}) }), _jsx(Route, { path: "/settings", element: _jsx(Settings, {}) })] }) }) }) })] }) }) }) }));
};
export default App;
